import {Component, HostListener, Injectable, OnInit, ViewChild} from '@angular/core';
import {CommonMessagesService} from "../../services/common-messages.service";
import {LoggedUser} from "../../common/logged-user.service";
import {OrganizationType} from "../../model/organization-type";
import {OrganizationTypesService} from "../../services/organization-types.service";
import {OrganizationTypePersistRequest} from "../../model/requests/organization-type-persist-request";
import {ComponentCanDeactivate} from "../../common/can-deactivate/component-can-deactivate";

@Component({
  selector: 'app-organization-types-list',
  templateUrl: './organization-types-list.component.html',
  styleUrls: ['./organization-types-list.component.scss']
})
@Injectable({
  providedIn: 'root'
})
export class OrganizationTypesListComponent implements OnInit, ComponentCanDeactivate {

  @ViewChild('table')
  public table: any;

  public organizationTypes: OrganizationType[];
  public totalOrganizationTypes: number;
  public loadingOrganizationTypes: boolean;

  public rows: number = 10;
  public first: number = 0;

  public filtersVisible: boolean;
  public globalFilter: string;

  public newOrganizationTypeName: string;
  public addOrganizationType: boolean;

  public persistingChangesCreate: boolean;
  public persistingChangesEdit: boolean;

  private static forbiddenHttpError: number = 403;

  private mode: 'create' | 'edit';

  private clonedOrganizationTypes: Map<string, string> = new Map<string, string>();


  constructor(private organizationTypesService: OrganizationTypesService,
              private loggedUser: LoggedUser,
              private commonMessagesService: CommonMessagesService) {
    this.mode = 'create';
    this.addOrganizationType = false;
  }

  ngOnInit(): void {
    this.organizationTypes = new Array(this.rows);
    this.loadOrganizationTypes();
  }

  loadOrganizationTypes(): void {
    this.loadingOrganizationTypes = true;
    this.organizationTypes = new Array(this.rows);
    this.organizationTypesService.getOrganizationTypes().subscribe(response => {
      this.organizationTypes = response.organizationTypes;
      this.totalOrganizationTypes = response.organizationTypes.length;
      this.loadingOrganizationTypes = false;
    });
  }

  confirmAndDelete(organizationType: OrganizationType): void {
    this.commonMessagesService.showDeleteRequestConfirmation(() => {
      this.deleteOrganizationType(organizationType);
    });
  }

  deleteOrganizationType(organizationType: OrganizationType): void {
    if (organizationType.readOnly) {
      this.commonMessagesService.showErrorWhileDeletingMessage(OrganizationTypesListComponent.forbiddenHttpError);
      return;
    }
    this.organizationTypesService.deleteOrganizationType(organizationType.organizationTypeUuid).toPromise().then(() => {
      this.commonMessagesService.showSuccessfullyDeletedMessage();
      this.loadOrganizationTypes();
    }, error => {
      this.commonMessagesService.showErrorWhileDeletingMessage(error.status);
    });
  }

  onRowEditInit(organizationType: OrganizationType): void {
    this.clonedOrganizationTypes.set(organizationType.organizationTypeUuid, organizationType.name);
  }

  onRowEditCancel(organizationType: OrganizationType, index: number): void {
    this.organizationTypes[index].name = this.clonedOrganizationTypes.get(organizationType.organizationTypeUuid);
    this.clonedOrganizationTypes.delete(organizationType.organizationTypeUuid);
    this.persistingChangesEdit = false;
  }

  onRowEditSave(organizationType: OrganizationType, index: number): void {
    this.persistingChangesEdit = true;
    if (organizationType.readOnly) {
      this.commonMessagesService.showErrorWhileDeletingMessage(OrganizationTypesListComponent.forbiddenHttpError);
      return;
    }
    this.mode = 'edit';

    const persistData: OrganizationTypePersistRequest = this.buildPersistData(organizationType);
    this.organizationTypesService.editOrganizationType(persistData).toPromise().then(() => {
      this.clonedOrganizationTypes.delete(organizationType.organizationTypeUuid);
      this.commonMessagesService.showSuccessfullyUpdatedMessage();
      this.persistingChangesEdit = false;
      this.mode = 'create';
    }).catch(error => {
      this.onRowEditCancel(organizationType, index);
      this.commonMessagesService.showPersistingError(this.mode, error, persistData.name);
      this.mode = 'create';
    });
  }

  onRowCreateSave(): void {
    this.persistingChangesCreate = true;
    let organizationType: OrganizationType = new OrganizationType();
    organizationType.name = this.newOrganizationTypeName;
    const persistData: OrganizationTypePersistRequest = this.buildPersistData(organizationType);
    this.newOrganizationTypeName = undefined;

    this.organizationTypesService.createOrganizationType(persistData).toPromise().then(() => {
      this.commonMessagesService.showSuccessfullyCreatedMessage();
      this.persistingChangesCreate = false;
      this.addOrganizationType = false;
      this.loadOrganizationTypes();
    }).catch(error => {
      this.commonMessagesService.showPersistingError(this.mode, error, persistData.name);
      this.persistingChangesCreate = false;
      this.addOrganizationType = false;
    });
  }

  onRowCreateCancel(): void {
    this.addOrganizationType = false;
    this.newOrganizationTypeName = undefined;
  }

  private buildPersistData(organizationType: OrganizationType): OrganizationTypePersistRequest {
    return {
      name: organizationType.name?.trim(),
      organizationTypeUuid: this.mode === 'edit' ? organizationType.organizationTypeUuid : null
    };
  }

  resetFilters(): void {
    this.globalFilter = "";
    this.totalOrganizationTypes = this.organizationTypes.length;
  }

  applyFilters(): void {
    this.table.filter(this.globalFilter.trim(), 'name', 'contains');
    this.first = 0;
    this.filtersVisible = false;
  }

  onFilter(event: any): void {
    this.totalOrganizationTypes = event.filteredValue.length;
  }

  validate(event, id): void {
    let element = document.getElementById(id);
    if (event == '' || event.length > 255) {
      let mainRed = getComputedStyle(element).getPropertyValue('--main-red');
      element.setAttribute("style", "border-color:" + mainRed);
    } else {
      let primaryColor = getComputedStyle(element).getPropertyValue('--primary-color');
      element.setAttribute("style", "border-color:" + primaryColor);
    }
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return !this.addOrganizationType && this.clonedOrganizationTypes.size == 0;
  }

}
